import React, { FC } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import { Button } from "@natera/form";
import { LoadingContext } from "@natera/platform/lib/components/context";
import Svg from "@natera/material/lib/svg";
import { TestType } from "@app/provider/testData/types";
import { routes } from "@app/routing";
import { TestResultContext } from "@app/provider";
import { BlueHeaderContent } from "@app/components/ui/layout";
import DocumentsMagnifyingGlass from "@assets/svg/documents-magnifying-glass.svg";
import UnderstandingYourResultsContent from "./understandingYourResultsContent";
import useTestResultsTexts from "@app/hooks/useTestResultsTexts";
import getSupplementalVideoUid from "@app/utils/getSupplementalVideoUid";
import { TestResult } from "@app/provider/testResult";
import { BusinessUnits } from "@app/provider/drawRequest";
import {
  UnderstandingYourResultsDefaultContent,
  UnderstandingYourResultsOncologyContent,
} from "./contentType";
import { CommonErrorNotification } from "@app/components";
import { getReturnRouteFromOrigin } from "@app/utils/navigationOrigin";

import "./understandingYourResults.scss";

const messages = defineMessages({
  understandingYourResultsHeadline: {
    id: "understandingYourResultsHeadline",
    defaultMessage: "Understanding Your Results",
  },
  understandingYourResultsContinueButton: {
    id: "understandingYourResultsContinueButton",
    defaultMessage: "Continue to Results",
  },
  understandingYourResultsBackButton: {
    id: "understandingYourResultsBackButton",
    defaultMessage: "Back",
  },
});

type Params = {
  orderUidPublic: string;
  testUid: string;
  testDocumentUid: string;
};

const getTestTypeFlags = (testResult?: TestResult) => ({
  isEmpower: testResult?.testType === TestType.EMPOWER,
  isVistara: testResult?.testType === TestType.VISTARA,
  isSGNIPT: testResult?.testType === TestType.SINGLE_GENE_NIPT,
  isProspera: testResult?.testType === TestType.PROSPERA,
  isRenasight: testResult?.testType === TestType.RENASIGHT,
});

const UnderstandingYourResultsPage: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();

  const { orderUidPublic, testUid, testDocumentUid } = useParams<
    keyof Params
  >() as Params;

  const {
    testResult,
    getTestResult,
    getTestResultError,
    getSupplementalMaterials,
    isLoading,
  } = React.useContext(TestResultContext);

  React.useEffect(() => {
    const orderUidAndTestUidExist = orderUidPublic && testUid;
    const getSuppsIfTestResultsExist = async (
      testResult$: TestResult | undefined
    ) => {
      if (testResult$) {
        const uid = getSupplementalVideoUid(testResult$);
        uid && (await getSupplementalMaterials(orderUidPublic, testUid, uid));
      }
    };

    if (orderUidAndTestUidExist && !getTestResultError && !isLoading) {
      getTestResult(orderUidPublic, testUid).then(getSuppsIfTestResultsExist);
    }
  }, []);

  const testResultsTexts = useTestResultsTexts();

  const headlineByTestType =
    testResult?.testType &&
    testResultsTexts.understandingResultsHeadline(
      testResult?.testType,
      testResult?.businessUnit
    );
  const secondParagraphByTestType =
    testResult?.testType &&
    testResultsTexts.understandingResultsSecondParagraph(testResult?.testType);

  const { isEmpower, isVistara, isSGNIPT, isProspera, isRenasight } =
    getTestTypeFlags(testResult);
  const isOncologyTestType =
    testResult?.businessUnit === BusinessUnits.ONCOLOGY;

  const openTestResult = () => {
    navigate(
      routes.testResultPdfPage(orderUidPublic, testUid, testDocumentUid),
      {
        state: location.state,
      }
    );
  };

  const handleBack = () => {
    const backTo = getReturnRouteFromOrigin(location.state, routes.home);
    if (backTo !== routes.home) {
      navigate(backTo);
      return;
    }
    window.history.length > 2 ? navigate(-1) : navigate(routes.home);
  };

  const renderContent = () => {
    if (isOncologyTestType || isSGNIPT) {
      return testResult ? (
        <UnderstandingYourResultsOncologyContent
          testType={testResult.testType}
          isReflexed={testResult?.isReflexed}
        />
      ) : null;
    }

    const renderTestSpecificContent = () => {
      if (isEmpower || isVistara) {
        return (
          <UnderstandingYourResultsDefaultContent
            testType={testResult?.testType}
          />
        );
      }
      if (isProspera || isRenasight) {
        return <Svg content={DocumentsMagnifyingGlass} />;
      }
      return null;
    };

    return (
      <div className="understanding-your-results__content">
        <h3>{headlineByTestType}</h3>

        {renderTestSpecificContent()}

        <div className="understanding-your-results__content__wrapper">
          {secondParagraphByTestType && <p>{secondParagraphByTestType}</p>}
          {testResult && (
            <UnderstandingYourResultsContent testResult={testResult} />
          )}
        </div>
      </div>
    );
  };

  return (
    <BlueHeaderContent
      blueHeaderTitle={intl.formatMessage(
        messages.understandingYourResultsHeadline
      )}
      crossHeaderButton={handleBack}
      fullScreen
    >
      <div
        className="understanding-your-results"
        data-testid="understanding-your-results-page"
      >
        <section className="understanding-your-results__container">
          <LoadingContext isLoading={isLoading}>
            {getTestResultError ? (
              <CommonErrorNotification />
            ) : (
              <>
                <div className="understanding-your-results__container__header">
                  <h3 className="understanding-your-results__container__title">
                    {headlineByTestType}
                  </h3>
                  <div className="understanding-your-results__container__buttons-wrapper">
                    <Button
                      onClick={handleBack}
                      data-automation_id="back_button"
                    >
                      {intl.formatMessage(
                        messages.understandingYourResultsBackButton
                      )}
                    </Button>
                    <Button
                      unelevated
                      onClick={openTestResult}
                      data-automation_id="continue-to-results_button"
                    >
                      {intl.formatMessage(
                        messages.understandingYourResultsContinueButton
                      )}
                    </Button>
                  </div>
                </div>
                {renderContent()}
                <div className="understanding-your-results__container__footer">
                  <Button
                    className="understanding-your-results__container__action-button"
                    unelevated
                    onClick={openTestResult}
                    data-automation_id="continue-to-results_button"
                  >
                    {intl.formatMessage(
                      messages.understandingYourResultsContinueButton
                    )}
                  </Button>
                  <Button
                    className="understanding-your-results__container__action-button"
                    onClick={handleBack}
                    data-automation_id="back_button"
                  >
                    {intl.formatMessage(
                      messages.understandingYourResultsBackButton
                    )}
                  </Button>
                </div>
              </>
            )}
          </LoadingContext>
        </section>
      </div>
    </BlueHeaderContent>
  );
};

export default UnderstandingYourResultsPage;
